/* eslint-disable no-shadow */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable spaced-comment */
/* eslint-disable react/require-default-props */
import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  Button,
  Icon,
  useDisclosure,
  Tooltip,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { RiEditLine } from 'react-icons/ri';
import { useLoading, Oval } from '@agney/react-loading';

import { apllyToast } from '../../../Global/Toast2.0';
import getValidationErrors from '../../../../utils/getValidationErrors';
import Input from '../../../Global/Input';
import TextArea from '../../../Global/TextArea';
import Select from '../../../Global/SelectRelease';
import { ModalComponent } from '..';
import DriverKilometersService, {
  IDriverKilometerUpdate,
} from '../../../../services/driverKilometersService';

interface IModalProps {
  id_driver_kilometer: string;
  handleEditDriverKilometer: (id: string, data: IDriverKilometerUpdate) => void;
}

export function ModalEditDriverKilometer({
  id_driver_kilometer,
  handleEditDriverKilometer,
}: IModalProps) {
  const formRef = useRef<FormHandles>(null);
  const { onOpen, isOpen, onClose } = useDisclosure();
  const componentRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [reasonItem, setReason] = useState('');

  const [kmStartValue, setKmStartValue] = useState(0);
  const [kmEndValue, setKmEndValue] = useState(0);
  const [kmTraveledValue, setKmTraveledValue] = useState(0);

  useEffect(() => {
    if (isOpen && id_driver_kilometer) {
      setLoadingData(true);
      DriverKilometersService.getById(id_driver_kilometer)
        .then(response => {
          const kmStart = Number(response.km_start);
          const kmEnd = Number(response.km_end);
          const kmTraveled = Number(response.km_traveled);

          setKmStartValue(kmStart);
          setKmEndValue(kmEnd);
          setKmTraveledValue(kmTraveled);
          setReason(response.reason);

          formRef.current?.setData({
            date: response.date.substring(0, 16),
            km_start: kmStart,
            km_end: kmEnd,
            km_traveled: kmTraveled,
            destination: response.destination,
            observation: response.observation,
          });

          setLoadingData(false);
        })
        .catch(err => {
          console.error(err);
          apllyToast('error', 'Erro ao carregar dados da quilometragem');
          setLoadingData(false);
          onClose();
        });
    }
  }, [isOpen, id_driver_kilometer, onClose]);

  const handleSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        setLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          date: Yup.date().required('Data é obrigatória'),
          km_start: Yup.number()
            .required('Km inicial é obrigatório')
            .min(0, 'Km inicial deve ser maior ou igual a zero'),
          km_end: Yup.number()
            .required('Km final é obrigatório')
            .min(0, 'Km final deve ser maior ou igual a zero')
            .moreThan(
              Yup.ref('km_start'),
              'Km final deve ser maior que km inicial',
            ),
          km_traveled: Yup.number()
            .required('Km percorrido é obrigatório')
            .positive('Km percorrido deve ser positivo'),
          destination: Yup.string().required('Destino é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const formatData: IDriverKilometerUpdate = {
          km_end: Number(data.km_end),
          km_start: Number(data.km_start),
          km_traveled: Number(data.km_traveled),
          destination: String(data.destination),
          observation: data.observation ? String(data.observation) : '',
          reason: reasonItem,
          date: String(data.date),
        };

        handleEditDriverKilometer(id_driver_kilometer, formatData);
        onClose();
        setLoading(false);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setLoading(false);
          formRef.current?.setErrors(errors);
          return;
        }
        setLoading(false);
      }
    },
    [handleEditDriverKilometer, id_driver_kilometer, onClose, reasonItem],
  );

  const { containerProps, indicatorEl } = useLoading({
    loading,
    indicator: <Oval />,
  });

  const handleKmStart = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setKmStartValue(Number(e.target.value));
    },
    [],
  );

  const handleKmEnd = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setKmEndValue(Number(e.target.value));
  }, []);

  useEffect(() => {
    if (kmEndValue > kmStartValue) {
      setKmTraveledValue(kmEndValue - kmStartValue);
    }
  }, [kmEndValue, kmStartValue]);

  const handleReason = useCallback((value: string) => {
    setReason(value);
  }, []);

  return (
    <>
      <Tooltip hasArrow label="Editar quilometragem">
        <Button
          as="a"
          size="sm"
          fontSize="sm"
          colorScheme="purple"
          onClick={onOpen}
          cursor="pointer"
        >
          <Icon as={RiEditLine} fontSize="16" />
        </Button>
      </Tooltip>
      <ModalComponent
        title="Editar quilometragem de condutor"
        isOpen={isOpen}
        onClose={onClose}
      >
        <ModalBody>
          {loadingData ? (
            <div>Carregando...</div>
          ) : (
            <Form ref={formRef} onSubmit={handleSubmit}>
              <p>Data</p>
              <Input type="datetime-local" name="date" />

              <p>Quilometragem inicial</p>
              <Input
                onChange={handleKmStart}
                value={kmStartValue}
                type="number"
                name="km_start"
                placeholder="Exemplo: 12000"
              />

              <p>Quilometragem final</p>
              <Input
                onChange={handleKmEnd}
                value={kmEndValue}
                type="number"
                name="km_end"
                placeholder="Exemplo: 12350"
              />

              <p>Quilômetros percorridos</p>
              <Input
                value={kmTraveledValue}
                type="number"
                name="km_traveled"
                placeholder="Calculado automaticamente"
                variant="filled"
                bg="gray.800"
                isReadOnly
              />

              <Select
                name="reason"
                label="Qual o motivo?"
                value={reasonItem}
                onChange={e => {
                  handleReason(e.target.value);
                }}
                options={[
                  {
                    value: 'Consultoria de venda',
                    label: 'Consultoria de venda',
                  },
                  { value: 'Entrega de produto', label: 'Entrega de produto' },
                  {
                    value: 'Deslocamento a trabalho',
                    label: 'Deslocamento a trabalho',
                  },
                  {
                    value: 'Visita técnica',
                    label: 'Visita técnica',
                  },
                  {
                    value: 'Reunião externa',
                    label: 'Reunião externa',
                  },
                  {
                    value: 'Outros',
                    label: 'Outros',
                  },
                ]}
              />

              <p>Qual o destino?</p>
              <Input name="destination" placeholder="Ex: São Paulo - Centro" />

              <p>Observações (opcional)</p>
              <TextArea
                name="observation"
                placeholder="Ex: reunião com cliente ABC."
              />

              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Fechar
                </Button>
                <Button colorScheme="blue" type="submit">
                  {loading ? (
                    <div {...containerProps} ref={componentRef}>
                      {indicatorEl}
                    </div>
                  ) : (
                    'Atualizar'
                  )}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </ModalBody>
      </ModalComponent>
    </>
  );
}
